// What each supplier sees in their portal. Stored on suppliers/{id}.permissions
// and applied server-side when their views are built
// (functions/supplierViews.js keeps the same key list — change both).
//
// A supplier always sees the activities they're the supplier on, their
// bookings and their approval requests. These add to that:

export const PERMISSION_LABELS = {
  tourOverview: 'Tour Overview',
  tourLeaderInfo: 'Tour Leader Contacts',
  teamInfo: 'Team Information',
  teamLists: 'Team Lists',
  fixtureInfo: 'Match Details',
  participantInfo: 'Participants',
  participantPassports: 'Passports & Contact Details',
  rooming: 'Rooming',
  generalItinerary: 'Full Itinerary',
  sandwichOrderResponses: 'Sandwich Order Responses',
  tgbOrderResponses: 'TGB Order Responses',
  belaCafeResponses: 'Bela Café Responses',
  canEditItinerary: 'Can Edit Fixture/Session Details',
  canLogExpenses: 'Can Log Expenses',
};

export const PERMISSION_HINTS = {
  tourOverview: 'Group type and numbers',
  tourLeaderInfo: 'Names, emails and phones, with the teams each leads',
  teamInfo: 'Squads, ages and kits, plus player names and dates of birth',
  teamLists: 'Player names and dates of birth in each team',
  fixtureInfo: 'Teams, opposition and kits on their Fixtures activities',
  participantInfo: 'Names, dates of birth, dietary and medical needs',
  participantPassports: 'Passport numbers, guardians and contact details. Hotels only.',
  rooming: 'Who is in each room',
  generalItinerary: 'Every activity on the tour, not just theirs',
  canEditItinerary: 'Change the time and notes on their own activities',
  canLogExpenses: 'Add expenses to the tour',
};

export const DEFAULT_PERMISSIONS = Object.fromEntries(Object.keys(PERMISSION_LABELS).map((k) => [k, false]));

/**
 * Permissions with every key present. Passports used to come with
 * Participants; they now have their own switch, which accommodation
 * suppliers inherit until it's set. Same rule as the server.
 */
export function effectivePermissions(supplier) {
  const stored = supplier?.permissions || {};
  const out = { ...DEFAULT_PERMISSIONS };
  for (const key of Object.keys(DEFAULT_PERMISSIONS)) out[key] = stored[key] === true;
  if (stored.participantPassports === undefined) {
    out.participantPassports = stored.participantInfo === true && supplier?.category === 'accommodation';
  }
  return out;
}

export const CATEGORY_DEFAULTS = {
  accommodation: {
    ...DEFAULT_PERMISSIONS,
    tourOverview: true, tourLeaderInfo: true, teamInfo: true,
    participantInfo: true, participantPassports: true, rooming: true, generalItinerary: true,
  },
  football: {
    ...DEFAULT_PERMISSIONS,
    tourOverview: true, tourLeaderInfo: true, teamInfo: true, fixtureInfo: true, participantInfo: true,
  },
  transport: {
    ...DEFAULT_PERMISSIONS,
    tourOverview: true, tourLeaderInfo: true, generalItinerary: true,
  },
  food_hotel: {
    ...DEFAULT_PERMISSIONS,
    tourOverview: true, tourLeaderInfo: true, participantInfo: true, generalItinerary: true,
  },
  food_sandwich: {
    ...DEFAULT_PERMISSIONS,
    tourOverview: true, tourLeaderInfo: true, participantInfo: true,
    sandwichOrderResponses: true, tgbOrderResponses: true,
  },
  food_tgb: {
    ...DEFAULT_PERMISSIONS,
    tourOverview: true, tourLeaderInfo: true, participantInfo: true, tgbOrderResponses: true,
  },
  food_cafe: {
    ...DEFAULT_PERMISSIONS,
    tourOverview: true, tourLeaderInfo: true, participantInfo: true, belaCafeResponses: true,
  },
  activity: {
    ...DEFAULT_PERMISSIONS,
    tourOverview: true, tourLeaderInfo: true, participantInfo: true,
  },
  contractor_football: {
    ...DEFAULT_PERMISSIONS,
    tourOverview: true, tourLeaderInfo: true, teamInfo: true, fixtureInfo: true,
    participantInfo: true, generalItinerary: true, canEditItinerary: true, canLogExpenses: true,
  },
};

export const CATEGORY_PRESET_LABELS = {
  accommodation: 'Accommodation',
  football: 'Football Provider',
  transport: 'Transport',
  food_hotel: 'Food – Hotel Catering',
  food_sandwich: 'Food – Sandwiches',
  food_tgb: 'Food – TGB',
  food_cafe: 'Food – Café',
  activity: 'Activity Provider',
  contractor_football: 'Contractor – Football Ops (David)',
};

export const PERMISSION_GROUPS = [
  { label: 'Tour', keys: ['tourOverview', 'tourLeaderInfo'] },
  { label: 'Teams & Fixtures', keys: ['teamInfo', 'teamLists', 'fixtureInfo'] },
  { label: 'Participants', keys: ['participantInfo', 'participantPassports', 'rooming'] },
  { label: 'Itinerary', keys: ['generalItinerary'] },
  { label: 'Food Orders', keys: ['sandwichOrderResponses', 'tgbOrderResponses', 'belaCafeResponses'] },
  { label: 'Contractor Capabilities', keys: ['canEditItinerary', 'canLogExpenses'] },
];
